import { Search } from "lucide-react";
import React, { useEffect, useState } from "react";

interface ConsumerSearchInputProps {
  onSearchChange: (searchTerm: string) => void;
  placeholder?: string;
  value?: string; // Optional controlled value
  disabled?: boolean;
}

export const ConsumerSearchInput: React.FC<ConsumerSearchInputProps> = ({
  onSearchChange,
  placeholder = "Search consumers...",
  value: externalValue,
  disabled,
}) => {
  const [internalValue, setInternalValue] = useState("");

  // Determine if component is controlled
  const isControlled = externalValue !== undefined;

  // Use external value if controlled, otherwise use internal state
  const searchValue = isControlled ? externalValue : internalValue;

  // Sync internal value with external value on mount/change
  useEffect(() => {
    if (externalValue !== undefined && !isControlled) {
      setInternalValue(externalValue);
    }
  }, [externalValue, isControlled]);

  // Debounced search - only applies to uncontrolled mode
  useEffect(() => {
    if (!isControlled) {
      const timeoutId = setTimeout(() => {
        onSearchChange(internalValue);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [internalValue, onSearchChange, isControlled]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (isControlled) {
      // In controlled mode, parent handles the value
      // Parent's setState will update the value immediately
      setInternalValue(newValue); // Update internal for smooth typing
      onSearchChange(newValue); // Notify parent (parent's debounce or immediate update)
    } else {
      // In uncontrolled mode, update internal state (debounce happens in effect)
      setInternalValue(newValue);
    }
  };

  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
      <input
        type="text"
        placeholder={placeholder}
        value={searchValue}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full h-12 pl-10 pr-4 border-2 border-blue-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${disabled ? "bg-gray-100 cursor-not-allowed border-gray-300" : ""}`}
      />
    </div>
  );
};
